import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/**
 * IDENT (dent-it.ru) integration block.
 * Allows the salon owner to enable/disable a pull-based integration with the
 * IDENT desktop app. We expose a salon-specific URL + integration key that the
 * clinic pastes into IDENT → Settings → Integrations → HTTP.
 */
export const IdentIntegrationBlock = ({ API }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/integrations/ident/status`);
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/integrations/ident/enable`);
      setStatus(s => ({ ...(s || {}), enabled: true, integration_key: res.data.integration_key, base_url: res.data.base_url, tickets_endpoint: res.data.endpoints.GetTickets }));
      toast.success("IDENT-интеграция активирована");
      setShowKey(true);
    } catch (e) {
      toast.error("Не удалось активировать интеграцию");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Отключить IDENT? Текущий ключ будет отозван — IDENT перестанет получать новые заявки.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/integrations/ident/disable`);
      setStatus({ enabled: false, integration_key: null, base_url: status?.base_url, tickets_endpoint: status?.tickets_endpoint, total_polls: 0, polls_last_24h: 0 });
      toast.success("IDENT-интеграция отключена");
      setShowKey(false);
    } catch {
      toast.error("Не удалось отключить интеграцию");
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async () => {
    if (!window.confirm("Перевыпустить ключ? Старый ключ перестанет работать — нужно обновить его в IDENT.")) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/integrations/ident/enable`); // enable also rotates
      setStatus(s => ({ ...(s || {}), enabled: true, integration_key: res.data.integration_key }));
      toast.success("Новый ключ выпущен");
      setShowKey(true);
    } catch {
      toast.error("Не удалось перевыпустить ключ");
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} скопировано`);
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  if (loading) {
    return (
      <div className="text-xs text-gray-500" data-testid="ident-loading">
        Загрузка статуса IDENT…
      </div>
    );
  }

  const enabled = !!status?.enabled;

  return (
    <div data-testid="ident-integration-block" className="bg-white border-2 border-gray-300 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold flex items-center gap-2">
          🦷 IDENT (dent-it.ru)
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold text-white ${enabled ? "bg-green-600" : "bg-gray-400"}`}>
            {enabled ? "АКТИВНО" : "ОТКЛЮЧЕНО"}
          </span>
        </h4>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Автоматическая передача заявок из AI-чата в IDENT. IDENT периодически опрашивает наш сервер и забирает новые заявки.
      </p>

      {!enabled ? (
        <div className="space-y-2">
          <Button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            data-testid="ident-enable-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {busy ? "Активируем…" : "Подключить IDENT"}
          </Button>
          <p className="text-[11px] text-gray-500">
            Подключение бесплатное. После активации получите URL и ключ, которые нужно вставить в настройки IDENT.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium text-gray-700">URL (вставить в IDENT)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={status?.base_url || ""}
                readOnly
                data-testid="ident-base-url"
                className="font-mono text-xs border-2 border-black"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(status?.base_url || "", "URL")}
                data-testid="ident-copy-url"
              >
                Копировать
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-700">Integration Key</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={showKey ? (status?.integration_key || "") : "••••••••••••••••"}
                readOnly
                data-testid="ident-key"
                className="font-mono text-xs border-2 border-black"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowKey(s => !s)}
                data-testid="ident-toggle-show"
              >
                {showKey ? "Скрыть" : "Показать"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(status?.integration_key || "", "Ключ")}
                data-testid="ident-copy-key"
              >
                Копировать
              </Button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              Храните ключ в секрете. Если он скомпрометирован — нажмите «Перевыпустить».
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-gray-800">
            <p className="font-semibold mb-1">Как настроить в IDENT:</p>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Откройте IDENT → Настройки → Интеграции → HTTP</li>
              <li>Вставьте URL выше</li>
              <li>В поле «Ключ интеграции» (или «IDENT-Integration-Key») вставьте Integration Key</li>
              <li>Нажмите «Проверить» — должен пройти тест</li>
              <li>Сохраните. IDENT начнёт опрашивать каждые 1-5 минут</li>
            </ol>
            <p className="mt-2">
              📘{" "}
              <a
                href="https://help.dent-it.ru/help/-variant-s-http"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline"
              >
                Полная инструкция IDENT
              </a>
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-gray-50 rounded p-2 border" data-testid="ident-stat-polls-24h">
              <div className="text-gray-500">Опросов за 24ч</div>
              <div className="font-bold text-lg">{status?.polls_last_24h ?? 0}</div>
            </div>
            <div className="bg-gray-50 rounded p-2 border" data-testid="ident-stat-total-polls">
              <div className="text-gray-500">Всего опросов</div>
              <div className="font-bold text-lg">{status?.total_polls ?? 0}</div>
            </div>
            <div className="bg-gray-50 rounded p-2 border" data-testid="ident-stat-tickets">
              <div className="text-gray-500">Заявок передано</div>
              <div className="font-bold text-lg">{status?.total_tickets_returned ?? 0}</div>
            </div>
          </div>

          {status?.last_poll_at && (
            <p className="text-[11px] text-gray-500" data-testid="ident-last-poll">
              Последний опрос: {new Date(status.last_poll_at).toLocaleString("ru-RU")}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRotate}
              disabled={busy}
              data-testid="ident-rotate-btn"
            >
              Перевыпустить ключ
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDisable}
              disabled={busy}
              data-testid="ident-disable-btn"
              className="border-red-400 text-red-600 hover:bg-red-50"
            >
              Отключить
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdentIntegrationBlock;
